import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";

/** Decisão U1: seletores, chips, datas e switches aplicam após 300 ms; texto após 400 ms (ou no Enter). */
export const DISCRETE_DELAY_MS = 300;
export const TEXT_DELAY_MS = 400;

type FilterValues = Record<string, unknown>;

export interface AutoApplyOptions<T extends FilterValues> {
  /** Filtros aplicados hoje (URL ou estado do pai). Mudou por fora → o rascunho acompanha. */
  value: T;
  /** Valores de "Limpar filtros". */
  defaults: T;
  /** Recebe o rascunho quando ele muda de fato em relação ao aplicado. */
  onApply: (next: T) => void;
  /** Opcional: o pai cuida do limpar (ex.: preserva o pacote aberto). Sem ele, chama `onApply(defaults)`. */
  onClear?: () => void;
  /** Campos de texto livre (debounce maior e Enter aplica na hora). */
  textFields?: Array<keyof T>;
  /** Mesma normalização que o pai aplica (trim, dígitos…), para comparar e não apagar o que se digita. */
  normalize?: (values: T) => T;
}

const identity = <T,>(v: T) => v;

/**
 * Comportamento único das barras de filtro (Antecipações, Conferência, Pacotes e Dados do Médico):
 * aplica sozinho com debounce, sem botão "Aplicar", e só chama `onApply` quando o filtro muda de fato
 * (uma busca por mudança efetiva).
 */
export function useAutoApplyFilters<T extends FilterValues>({ value, defaults, onApply, onClear, textFields = [], normalize = identity }: AutoApplyOptions<T>) {
  const keyOf = (v: T) => JSON.stringify(normalize(v));
  const [draft, setDraftState] = useState<T>(value);
  const draftRef = useRef<T>(value);
  const appliedKeyRef = useRef(keyOf(value));
  const [appliedKey, setAppliedKey] = useState(appliedKeyRef.current);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef({ onApply, onClear, normalize, defaults, textFields });
  latest.current = { onApply, onClear, normalize, defaults, textFields };

  const cancel = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);
  useEffect(() => cancel, [cancel]);

  const markApplied = useCallback((key: string) => {
    appliedKeyRef.current = key;
    setAppliedKey(key);
  }, []);
  const writeDraft = useCallback((next: T) => {
    draftRef.current = next;
    setDraftState(next);
  }, []);

  // Aplicado mudou por fora (URL, limpar pelo estado vazio, normalização que muda o valor): o rascunho acompanha.
  const valueKey = keyOf(value);
  useEffect(() => {
    if (valueKey === appliedKeyRef.current) return;
    cancel();
    markApplied(valueKey);
    writeDraft(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const flush = useCallback(() => {
    cancel();
    const current = draftRef.current;
    const key = JSON.stringify(latest.current.normalize(current));
    if (key === appliedKeyRef.current) return;
    markApplied(key);
    latest.current.onApply(current);
  }, [cancel, markApplied]);

  const schedule = (delay: number) => {
    cancel();
    timerRef.current = setTimeout(flush, delay);
  };

  /** Atualiza o rascunho e agenda a aplicação (300 ms; 400 ms se `text`). */
  const update = useCallback(
    (updater: (current: T) => T, kind: "text" | "discrete" = "discrete") => {
      const next = updater(draftRef.current);
      if (next === draftRef.current) return;
      writeDraft(next);
      schedule(kind === "text" ? TEXT_DELAY_MS : DISCRETE_DELAY_MS);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flush]
  );

  const setField = useCallback(
    <K extends keyof T>(name: K, fieldValue: T[K]) =>
      update((d) => ({ ...d, [name]: fieldValue }), latest.current.textFields.includes(name) ? "text" : "discrete"),
    [update]
  );

  /** Props de um campo de texto: valor, onChange com debounce de texto e Enter aplicando na hora. */
  const bindText = <K extends keyof T>(name: K) => ({
    value: (draft[name] ?? "") as string,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => update((d) => ({ ...d, [name]: event.target.value }), "text"),
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === "Enter") flush();
    },
  });

  const clear = useCallback(() => {
    cancel();
    const { defaults: d, onClear: clearFn, onApply: applyFn, normalize: norm } = latest.current;
    writeDraft(d);
    const key = JSON.stringify(norm(d));
    if (key === appliedKeyRef.current) return;
    markApplied(key);
    if (clearFn) clearFn();
    else applyFn(d);
  }, [cancel, markApplied, writeDraft]);

  const defaultsKey = keyOf(defaults);
  const hasActive = keyOf(draft) !== defaultsKey || appliedKey !== defaultsKey;

  return { draft, setField, update, bindText, flush, clear, hasActive };
}
